/* TMF629 customer and requester master data → the cockpit's two list types.
 *
 * Both mappings are mostly rename, with two exceptions worth stating.
 *
 * `Customer.template` is not on the customer resource. Which clarification
 * template a tenant gets is a configuration decision — it lives in the
 * configuration slice, keyed by tenant — and the customers screen shows it as a
 * column because an operator asking "why did this customer get an English
 * letter" is looking at that screen when they ask. The index is passed in.
 *
 * `Customer.residency` is the distinct country codes across the tenant's active
 * sites, which the API already derives as `siteCountryCode`. It is derived and
 * not declared for a reason: a residency field somebody types is a claim, and
 * the set of countries the sites are actually in is a fact.
 */

import type { components } from '@/shared/api/generated/openapi';
import { DASH } from '@/shared/api/http/map/format';
import type { Customer, RequesterMaster, Site } from '@/shared/domain/customer';

export type WireCustomer = components['schemas']['CustomerModel'];
export type WireRequester = components['schemas']['RequesterModel'];
export type WireSite = components['schemas']['GeographicSiteModel'];

/** `onboarding` → `Onboarding`. The wire uses lower-case status vocabularies and
    the screens are prose; capitalising here keeps every screen from doing it. */
function titled(value: string | null | undefined): string {
  if (!value) return DASH;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Customers, with the template each one's clarifications are written from.
 *
 * `templates` maps customer id → template id. A tenant with no entry shows a
 * dash rather than the name of the default template: the default is resolved by
 * fallback at send time, and printing its name in this column would say a
 * decision had been taken for this tenant when none has.
 *
 * A caller sees exactly one customer — row-level security sees to that — so this
 * list is one row long in practice. It is still a list, because the screen that
 * renders it is a table and a table of one is not a special case.
 */
export function toCustomers(
  wires: readonly WireCustomer[],
  templates: ReadonlyMap<string, string> = new Map(),
): readonly Customer[] {
  return wires.map((wire) => {
    // Absent and empty are the same claim here — no site has reported a country
    // — and the contract allows the key to be missing on a tenant with no sites.
    const siteCountries = wire.siteCountryCode ?? [];
    return {
      id: wire.id,
      name: wire.name,
      sites: wire.siteCount,
      // The sites' countries where there are any, the customer's own registered
      // country where there are not. A tenant with no sites yet has a country;
      // it has no residency footprint, and the two are different claims.
      residency:
        siteCountries.length > 0 ? siteCountries.join(', ') : (wire.countryCode ?? DASH),
      requesterIds: wire.requesterCount,
      template: templates.get(wire.id) ?? DASH,
      state: titled(wire.status),
    };
  });
}

function siteAddress(wire: WireSite): string {
  const street = [wire.place.streetName, wire.place.streetNr].filter(Boolean).join(' ');
  const locality = [wire.place.postcode, wire.place.city].filter(Boolean).join(' ');
  return [street, locality, wire.place.stateOrProvince].filter(Boolean).join(', ') || DASH;
}

export function toSites(wires: readonly WireSite[]): readonly Site[] {
  return wires.map(toSite);
}

export function toSite(wire: WireSite): Site {
  return {
    id: wire.id,
    customerId: wire.customerId,
    name: wire.name,
    code: wire.code ?? null,
    address: siteAddress(wire),
    country: wire.place.country,
    postcode: wire.place.postcode ?? null,
    city: wire.place.city ?? null,
    stateOrProvince: wire.place.stateOrProvince ?? null,
    streetName: wire.place.streetName ?? null,
    streetNr: wire.place.streetNr ?? null,
    status: wire.status,
    serviceabilityStatus: wire.serviceabilityStatus,
    version: wire.resourceVersion,
  };
}

/**
 * Requester masters, with the addresses that resolve an inbound sender to them.
 *
 * Only the active addresses. A deactivated address no longer resolves anything —
 * the intake predicate filters on `active` — so listing it would show an
 * operator a route that does not exist, which is exactly the mistake that gets
 * made when a requester leaves and their mail starts bouncing.
 *
 * `status` is the maintained state, not `eligible`. The two differ while a
 * requester is active but outside their validity period, and that difference is
 * the answer to "why was this person's request not auto-bound" — collapsing them
 * would delete the question.
 *
 * `version` is carried because retiring a requester is a `PATCH` behind an
 * `If-Match`, and the version the operator's row was read at is the only honest
 * answer to "which state did you mean to change".
 */
export function toRequesterMasters(
  wires: readonly WireRequester[],
): readonly RequesterMaster[] {
  return wires.map(toRequesterMaster);
}

/** One requester, for the write paths that answer with the single resource.
 *
 * The POST and the PATCH both return the requester they just changed, and
 * mapping that response is how the screen gets the new version without a second
 * read — which is also the only way to toggle a requester twice in a row
 * without a refetch in between. */
export function toRequesterMaster(wire: WireRequester): RequesterMaster {
  return {
    requesterMasterId: wire.id,
    customerId: wire.customerId,
    displayName: wire.displayName,
    emails: (wire.emailAddress ?? [])
      .filter((address) => address.active)
      .map((address) => address.email),
    status: titled(wire.status),
    version: wire.resourceVersion,
  };
}

/** Site id → its name, for the places a characteristic value points at.
 *
 * `deliveryLocation` is a `GeographicSiteRef`, and the MRI grid has one column:
 * without this the cell renders a uuid, which is findable but unreadable. Built
 * from the same site list used by the customers screen. */
export function siteNames(wires: readonly WireSite[]): ReadonlyMap<string, string> {
  return new Map(wires.map((wire) => [wire.id, wire.name]));
}
